// Host-vetting predicates — the shared SSRF bar for any account/repo-controlled
// URL we dial from OUR cluster (log sink, outbound webhook, MCP server, BYOK
// base URL). Pure + synchronous: string/shape checks over a hostname, no DNS,
// no sockets. The async DNS-rebind gate (a public NAME that RESOLVES private) is
// `resolveAndVetPublicHost` in `host-resolve.ts` beside this file — also pure,
// because its lookup is a REQUIRED injected parameter. The ONE production DNS
// binding is `@developerz/kit` `dnsLookupAll`; domain never imports `node:dns`.
//
// Lives in `@developerz/domain` (not `@developerz/billing`) so both the billing
// log-sink probe AND the policy `.maintainer.yml` schema can share ONE SSRF
// predicate without crossing the `policy → billing` dependency boundary. A
// single copy is the point: an
// SSRF blocklist that forks between two files drifts into a hole.

/**
 * Parse an IPv4 dotted-quad into octets, or null when it isn't one. STRICT:
 * exactly four decimal parts with no leading zeros — an octal-looking
 * `0177.0.0.1` must NOT match here; ambiguous forms are rejected wholesale in
 * {@link isForbiddenSinkHost} instead of being parsed base-by-base.
 */
function ipv4Octets(hostname: string): [number, number, number, number] | null {
  const m = /^(0|[1-9]\d{0,2})\.(0|[1-9]\d{0,2})\.(0|[1-9]\d{0,2})\.(0|[1-9]\d{0,2})$/.exec(
    hostname,
  );
  if (!m) return null;
  const octets = m.slice(1).map(Number) as [number, number, number, number];
  return octets.every((o) => o <= 255) ? octets : null;
}

/** True when `hostname` is an address literal (strict dotted-quad or IPv6). */
export function isIpLiteralHost(hostname: string): boolean {
  const host = hostname.replace(/^\[|\]$/g, '');
  return host.includes(':') || ipv4Octets(host) !== null;
}

/**
 * A DNS label we accept: LDH (letters/digits/hyphen, no leading/trailing
 * hyphen). Anything else — `%`-escapes, `_`, raw unicode, empty labels — is
 * refused outright: `postgres://` is a NON-special scheme, so WHATWG URL keeps
 * its host opaque (no punycoding, no percent-decoding) and a permissive check
 * here would diverge from whatever the driver's own parser later dials.
 */
const LDH_LABEL_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

/**
 * True when `hostname` is a literal address / name we refuse to dial (SSRF
 * posture): the connect runs from OUR cluster, so a URL pointing at localhost,
 * link-local (cloud metadata `169.254.169.254`), or a private range would reach
 * our own network.
 *
 * FAILS CLOSED on ambiguity: for a non-special scheme, WHATWG URL leaves
 * obfuscated IPv4 forms untouched in `hostname` — `0177.0.0.1` (octal),
 * `0x7f.0.0.1` (hex), `127.1` (short), `2130706433` (integer) — so anything
 * that is not a STRICT dotted-quad yet has a final label starting with a digit
 * (no real TLD does) is rejected outright rather than parsed base-by-base.
 * Name labels are further constrained to LDH. A public DNS name that RESOLVES
 * privately is the rebind case — the async resolve gate
 * (`resolveAndVetPublicHost`); callers of both must run this gate first.
 */
export function isForbiddenSinkHost(hostname: string): boolean {
  // Strip brackets and one trailing dot (an FQDN root dot would otherwise let
  // `127.0.0.1.` skirt the literal checks as a "name").
  const host = hostname
    .toLowerCase()
    .replace(/^\[|\]$/g, '')
    .replace(/\.$/, '');
  if (host === '' || host === 'localhost' || host.endsWith('.localhost')) return true;
  if (host.endsWith('.local') || host.endsWith('.internal')) return true;

  // IPv6 literals: loopback / unspecified / unique-local (fc00::/7) /
  // link-local (fe80::/10) / IPv4-embedded (recurse on the embedded v4).
  if (host.includes(':')) {
    if (host === '::' || host === '::1') return true;
    if (/^f[cd]/.test(host)) return true;
    if (/^fe[89ab]/.test(host)) return true;
    // IPv4 embedded in the low 32 bits — v4-mapped (::ffff:), deprecated
    // v4-compatible (::), and NAT64 (64:ff9b::) all carry a routable IPv4 there,
    // so `::7f00:1` and `64:ff9b::7f00:1` both decode to 127.0.0.1. Recurse on the
    // embedded v4 so a private one is forbidden too. URL may render the tail as
    // two hex groups (::ffff:c0a8:1) or keep it dotted — handle both.
    const embedded =
      /^(?:::ffff:|::|64:ff9b::)(?:([0-9a-f]{1,4}):([0-9a-f]{1,4})|(\d+\.\d+\.\d+\.\d+))$/.exec(
        host,
      );
    if (embedded) {
      if (embedded[3]) return isForbiddenSinkHost(embedded[3]);
      if (embedded[1] && embedded[2]) {
        const hi = Number.parseInt(embedded[1], 16);
        const lo = Number.parseInt(embedded[2], 16);
        return isForbiddenSinkHost(`${hi >> 8}.${hi & 0xff}.${lo >> 8}.${lo & 0xff}`);
      }
    }
    // Any other IPv6 is public-routable ONLY if it is a well-formed hex/colon
    // literal; anything stranger fails closed.
    return !/^[0-9a-f:.]+$/.test(host);
  }

  const v4 = ipv4Octets(host);
  if (v4) {
    const [a, b] = v4;
    if (a === 0 || a === 10 || a === 127) return true; // this-net / RFC1918 / loopback
    if (a === 169 && b === 254) return true; // link-local + cloud metadata
    if (a === 172 && b >= 16 && b <= 31) return true; // RFC1918
    if (a === 192 && b === 168) return true; // RFC1918
    if (a === 100 && b >= 64 && b <= 127) return true; // CGNAT (100.64/10)
    return false;
  }

  // Not a strict dotted-quad → must look like a plausible DNS name: every
  // label LDH, and a final label that does NOT start with a digit — that shape
  // can only be an obfuscated address (octal/hex/short/integer IPv4), never a
  // public DNS name.
  const labels = host.split('.');
  if (labels.some((label) => !LDH_LABEL_RE.test(label))) return true;
  const last = labels[labels.length - 1] ?? '';
  return /^[0-9]/.test(last);
}

// ─── the composed gate ───────────────────────────────────────────────────────

/**
 * Why a URL is not safe for the platform to dial. A named code, never a bare
 * `false`: three different refusals ("that isn't a URL", "we don't speak that
 * scheme", "that host is inside our network") have three different fixes, and a
 * caller that collapses them tells the account nothing it can act on.
 */
export type PublicHttpUrlRefusal = 'not_a_url' | 'forbidden_scheme' | 'forbidden_host';

export type PublicHttpUrlVerdict =
  | { ok: true; url: URL }
  | { ok: false; code: PublicHttpUrlRefusal; reason: string };

/**
 * The ONE SSRF gate for any account-supplied URL the platform dials FROM our
 * cluster — a BYOK `base_url`, a BYO-S3 endpoint, an outbound log sink. Parse,
 * allow only `http:`/`https:`, then vet the literal host with
 * {@link isForbiddenSinkHost}.
 *
 * This composition lives here rather than beside any one caller because a second
 * copy is how an SSRF blocklist forks, and the copy that got it wrong is the one
 * an attacker uses. `label` names the field in the refusal reason so the message
 * stays the caller's ("base_url is not a valid URL"), while the RULE stays this
 * function's.
 *
 * Run it at the seam where the value ENTERS (the capture/connect write), not at
 * fetch time: a stored forbidden endpoint is a row that looks connected and
 * fails forever.
 *
 * Literal-host gate only — a public NAME that RESOLVES private (DNS rebind) needs
 * the async resolve-and-vet gate (`resolveAndVetPublicHost` in `host-resolve.ts`,
 * with `@developerz/kit` `dnsLookupAll` injected); this module stays synchronous.
 * Callers of both run this one first.
 */
export function vetPublicHttpUrl(raw: string, label: string): PublicHttpUrlVerdict {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return { ok: false, code: 'not_a_url', reason: `${label} is not a valid URL` };
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return {
      ok: false,
      code: 'forbidden_scheme',
      reason: `${label} scheme must be http:// or https://`,
    };
  }
  if (isForbiddenSinkHost(url.hostname)) {
    return {
      ok: false,
      code: 'forbidden_host',
      reason: `${label} host must be publicly routable — localhost, link-local, and private ranges are refused`,
    };
  }
  return { ok: true, url };
}
